import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import {
  VariableSourceSettingsDto,
  VariableSourceSettingsMutatePayload,
  MutationOptions,
  MutationResult,
  QueryResult,
} from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';
import { buildDashboardUrl } from './dashboardUrlUtils';

export function createDashboardVariableSourceQueryClient(
  axios: AxiosInstance
): DashboardVariableSourceQueryClient {
  return new DashboardVariableSourceQueryClientImp(axios);
}

class DashboardVariableSourceQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public useVariableSources = (
    options?: QueryOptions<VariableSourceSettingsDto[]>
  ): QueryResult<VariableSourceSettingsDto[]> => {
    return useQuery<VariableSourceSettingsDto[], AxiosError>(
      [QueryKeys.GetVariableSources],
      () => this.getVariableSources(),
      options
    );
  };

  public useVariableSource = (
    id: string,
    options?: QueryOptions<VariableSourceSettingsDto>
  ): QueryResult<VariableSourceSettingsDto> => {
    return useQuery<VariableSourceSettingsDto, AxiosError>(
      [QueryKeys.GetVariableSource, id],
      () => this.getVariableSource(id),
      options
    );
  };

  public useCreateVariableSource = (
    options?: MutationOptions<VariableSourceSettingsDto, VariableSourceSettingsMutatePayload>
  ): MutationResult<VariableSourceSettingsDto, VariableSourceSettingsMutatePayload> => {
    return useMutation<VariableSourceSettingsDto, AxiosError, VariableSourceSettingsMutatePayload>(
      [QueryKeys.CreateVariableSource],
      (body: VariableSourceSettingsMutatePayload) => this.createVariableSource(body),
      options
    );
  };

  public useUpdateVariableSource = (
    options?: MutationOptions<
      VariableSourceSettingsDto,
      { id: string; body: VariableSourceSettingsMutatePayload }
    >
  ): MutationResult<
    VariableSourceSettingsDto,
    { id: string; body: VariableSourceSettingsMutatePayload }
  > => {
    return useMutation<
      VariableSourceSettingsDto,
      AxiosError,
      { id: string; body: VariableSourceSettingsMutatePayload }
    >(
      [QueryKeys.UpdateVariableSource],
      ({ id, body }: { id: string; body: VariableSourceSettingsMutatePayload }) =>
        this.updateVariableSource(id, body),
      options
    );
  };

  public useDeleteVariableSource = (
    options?: MutationOptions<boolean, string>
  ): MutationResult<boolean, string> => {
    return useMutation<boolean, AxiosError, string>(
      [QueryKeys.DeleteVariableSource],
      (id: string) => this.deleteVariableSource(id),
      options
    );
  };

  private getVariableSources = async (): Promise<VariableSourceSettingsDto[]> => {
    return await this.axios
      .get(buildDashboardUrl('/dashboard/api/variable-sources'))
      .then((response: AxiosResponse<VariableSourceSettingsDto[]>) => response.data);
  };

  private getVariableSource = async (id: string): Promise<VariableSourceSettingsDto> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/variable-sources/${id}`))
      .then((response: AxiosResponse<VariableSourceSettingsDto>) => response.data);
  };

  private createVariableSource = async (
    body: VariableSourceSettingsMutatePayload
  ): Promise<VariableSourceSettingsDto> => {
    return await this.axios
      .post(buildDashboardUrl('/dashboard/api/variable-sources'), body)
      .then((response: AxiosResponse<VariableSourceSettingsDto>) => response.data);
  };

  private updateVariableSource = async (
    id: string,
    body: VariableSourceSettingsMutatePayload
  ): Promise<VariableSourceSettingsDto> => {
    return await this.axios
      .put(buildDashboardUrl(`/dashboard/api/variable-sources/${id}`), body)
      .then((response: AxiosResponse<VariableSourceSettingsDto>) => response.data);
  };

  private deleteVariableSource = async (id: string): Promise<boolean> => {
    return await this.axios
      .delete(buildDashboardUrl(`/dashboard/api/variable-sources/${id}`))
      .then((response: AxiosResponse<boolean>) => response.data);
  };
}

export interface DashboardVariableSourceQueryClient {
  useVariableSources: (
    options?: QueryOptions<VariableSourceSettingsDto[]>
  ) => QueryResult<VariableSourceSettingsDto[]>;
  useVariableSource: (
    id: string,
    options?: QueryOptions<VariableSourceSettingsDto>
  ) => QueryResult<VariableSourceSettingsDto>;
  useCreateVariableSource: (
    options?: MutationOptions<VariableSourceSettingsDto, VariableSourceSettingsMutatePayload>
  ) => MutationResult<VariableSourceSettingsDto, VariableSourceSettingsMutatePayload>;
  useUpdateVariableSource: (
    options?: MutationOptions<
      VariableSourceSettingsDto,
      { id: string; body: VariableSourceSettingsMutatePayload }
    >
  ) => MutationResult<
    VariableSourceSettingsDto,
    { id: string; body: VariableSourceSettingsMutatePayload }
  >;
  useDeleteVariableSource: (
    options?: MutationOptions<boolean, string>
  ) => MutationResult<boolean, string>;
}
